import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import AppBar from '@material-ui/core/AppBar'
import ArrowLeft from '@material-ui/icons/ArrowLeft'
import Autosave from '../Autosave'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import CheckedIcon from '@material-ui/icons/CheckBoxOutlined'
import UncheckedIcon from '@material-ui/icons/CheckBoxOutlineBlankOutlined'
import DeleteIcon from '@material-ui/icons/DeleteOutlined'
import Drawer from '@material-ui/core/Drawer'
import FormControl from '@material-ui/core/FormControl'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import TodoTagInput from './TodoTagInput'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import moment from 'moment'
import DatePicker from '../DatePicker'
import { Confirm, ConfirmDelete } from '../confirmation/Confirm'
import AreaSelect from '../area/AreaSelect'
import RepeatInput from '../repeats/RepeatInput'
import {
  FOCUS,
  SELECTABLE_FOCUS,
  DELETE_PERMANENTLY_FOCUS,
  getFocusTitle
} from '../../data/focus'
import LOG from '../../logging/Logger'
import {
  MAX_LENGTH_TODO_DESCRIPTION,
  MAX_LENGTH_TODO_SUMMARY
} from '../../data/limits'
import TodoListItemAttachments from './TodoListItemAttachments'

const useStyles = makeStyles(theme => ({
  delete: {
    margin: theme.spacing(1)
  },
  fullWidth: {
    width: 'calc(100% - 64px)'
  },
  margin: {
    marginLeft: 32
  },
  label: {
    marginRight: 0
  },
  drawer: {
    margin: 24
  },
  drawerPaper: {
    [theme.breakpoints.up('sm')]: {
      width: '100%'
    },
    [theme.breakpoints.up('md')]: {
      width: '40%'
    }
  },
  name: {
    width: 'calc(100% - 32px)'
  },
  checkbox: {
    marginTop: 6,
    marginLeft: 32,
    [theme.breakpoints.down('xs')]: {
      marginLeft: 24
    }
  },
  sizeCheckboxIcon: {
    fontSize: 'x-large'
  },
  endAdornment: {
    paddingRight: 6
  }
}))

const TICKLER_LIST = FOCUS.SCHEDULED.id
const TICKLER_LIST_REPLACE_ON_SAVE = FOCUS.TODAY.id // This is where we want tasks to appear when they date is current

const TRASH_LIST = FOCUS.TRASH.id

const TODAY = moment()

const isNewer = (currentVersion, newVersion) => {
  if (currentVersion === undefined && newVersion === undefined) {
    return false
  }

  return currentVersion === undefined || currentVersion < newVersion
}

const TodoListItemEditContent = ({
  classes,
  hasStatus,
  hasList,
  saveAllAndClose,
  setSaveAllAndClose,
  handleCloseDrawer,
  todo,
  decrypt,
  getRepeatPattern,
  clientId,
  deleteTodo,
  redirectToProject,
  createProject,
  onClose,
  getProjectById,
  editTodo,
  getProjectsForAreas,
  getAvailableTags,
  getAreas,
  getTagsFromTodo,
  createAttachment,
  deleteAttachment,
  editAttachment,
  downloadAttachment,
  getTodoAttachments
}) => {
  LOG.debug('Render: TodoListItemEditContent')

  const theme = useTheme()

  const [version, setVersion] = useState(todo._version)

  const [name, setName] = useState(decrypt(todo.name) || '')
  const [description, setDescription] = useState(
    decrypt(todo.description) || ''
  )
  const [list, setList] = useState(todo.list)
  const [tags, setTags] = useState(getTagsFromTodo(todo))
  const [project, setProject] = useState(todo.project ? todo.project : '')
  const [area, setArea] = useState(todo.area ? todo.area : '')
  const [startDate, setStartDate] = useState(
    todo.start ? moment(todo.start) : null
  )
  const [repeatPattern, setRepeatPattern] = useState(getRepeatPattern(todo))

  const [checked, setChecked] = useState(todo.status === 'done')

  const [openStartPicker, setOpenStartPicker] = useState(false)
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  const [openConfirmConvertToProject, setOpenConfirmConvertToProject] =
    useState(false)

  const attachments = useMemo(() => {
    return getTodoAttachments(todo)
  }, [getTodoAttachments, todo])

  useEffect(() => {
    // Only overwrite if newer
    if (isNewer(version, todo._version)) {
      // If the client id hasn't changed, then it's our own change
      if (clientId !== todo.clientId) {
        setList(todo.list)
        setName(decrypt(todo.name) || '')
        setChecked(todo.status === 'done')
        setDescription(decrypt(todo.description) || '')
        setTags(getTagsFromTodo(todo))
        setProject(todo.project ? todo.project : '')
        setArea(todo.area ? todo.area : '')
        setStartDate(todo.start ? moment(todo.start) : null)
        setRepeatPattern(getRepeatPattern(todo)) // TODO : this won't update if changed externally
      }
      setVersion(todo._version)
    }
  }, [todo, clientId, decrypt, version, getRepeatPattern, getTagsFromTodo])

  const doConvertToProject = useCallback(async () => {
    const newProject = await createProject(name, description, area)
    deleteTodo(todo)
    redirectToProject(newProject.id)
  }, [
    area,
    description,
    name,
    todo,
    createProject,
    deleteTodo,
    redirectToProject
  ])

  const handleConvertToProject = useCallback(() => {
    setOpenConfirmConvertToProject(true)
  }, [])

  const handleChangeName = useCallback(event => {
    setName(event.target.value)
  }, [])

  const handleChangeDescription = useCallback(event => {
    setDescription(event.target.value)
  }, [])

  const handleSetTags = useCallback(newTags => {
    setTags(newTags.sort())
  }, [])

  const handleSelectList = useCallback(event => {
    if (event.target.value === TICKLER_LIST) {
      setOpenStartPicker(true)
    } else {
      setStartDate(null)
    }

    setList(event.target.value)
  }, [])

  const handleChangeRepeat = useCallback(value => {
    setRepeatPattern(value)
  }, [])

  const handleDelete = useCallback(() => {
    setOpenConfirmDelete(true)
  }, [])

  const handleDeleteOk = useCallback(async () => {
    if (DELETE_PERMANENTLY_FOCUS.includes(todo.list)) {
      await deleteTodo(todo)
    } else {
      setList(TRASH_LIST)
      setSaveAllAndClose(true)
    }
    setOpenConfirmDelete(false)
  }, [deleteTodo, todo, setSaveAllAndClose])

  const handleClose = useCallback(() => {
    setSaveAllAndClose(false)
    onClose()
  }, [onClose, setSaveAllAndClose])

  const handleSetArea = useCallback(
    newArea => {
      if (newArea !== area) {
        const currentProject = getProjectById(project)
        if (currentProject && currentProject.area !== newArea) {
          setProject('')
        }
      }
      setArea(newArea)
    },
    [area, getProjectById, project]
  )

  const handleSetProject = useCallback(
    e => {
      const newProjectId = e.target.value
      if (newProjectId !== project) {
        const newProject = getProjectById(newProjectId)
        if (newProject) {
          setArea(newProject.area)
        }
      }
      setProject(newProjectId)
    },
    [getProjectById, project]
  )

  const handleSave = useCallback(
    saveFields => {
      editTodo(
        todo.id,
        saveFields.name,
        saveFields.description,
        todo.status,
        saveFields.tags,
        todo.area,
        todo.project,
        todo.list,
        todo.start,
        getRepeatPattern(todo)
      )
    },
    [editTodo, getRepeatPattern, todo]
  )

  const handleSaveAllAndClose = useCallback(
    (saveFields, jumpFields) => {
      let status = todo.status
      if (jumpFields.checked) {
        status = 'done'
      } else if (!jumpFields.checked && todo.status === 'done') {
        status = null
      }
      editTodo(
        todo.id,
        saveFields.name,
        saveFields.description,
        status,
        saveFields.tags,
        jumpFields.area,
        jumpFields.project,
        jumpFields.list === TICKLER_LIST
          ? TICKLER_LIST_REPLACE_ON_SAVE
          : jumpFields.list,
        jumpFields.start ? jumpFields.start.format('YYYY-MM-DD') : null,
        jumpFields.repeatPattern
      )
      handleClose()
    },
    [editTodo, handleClose, todo.id, todo.status]
  )

  const renderProjects = useCallback(() => {
    const projects = getProjectsForAreas([area, todo.area])
    if (projects && projects.length > 0) {
      return (
        <Grid item xs={12}>
          <FormControl className={clsx(classes.margin, classes.fullWidth)}>
            <InputLabel shrink>Project</InputLabel>
            <Select
              MenuProps={{
                disablePortal: true
              }}
              value={project}
              label='List'
              onChange={e => handleSetProject(e)}
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {projects.map(projectItem => (
                <MenuItem key={projectItem.id} value={projectItem.id}>
                  {decrypt(projectItem.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )
    }
    return null
  }, [
    area,
    classes.fullWidth,
    classes.margin,
    decrypt,
    getProjectsForAreas,
    handleSetProject,
    project,
    todo.area
  ])

  return (
    <Autosave
      saveFields={{ name, description, tags }}
      jumpFields={{
        checked,
        list,
        project,
        area,
        start: startDate,
        repeatPattern
      }}
      saveJumpFields={saveAllAndClose}
      onSave={handleSave}
      onSaveAll={handleSaveAllAndClose}
      onSaveAllNoop={handleClose}
    >
      <AppBar position='static'>
        <Toolbar>
          <IconButton onClick={handleCloseDrawer}>
            <ArrowLeft />
          </IconButton>
          <Typography variant='h6' component='h6'>
            Edit Item
          </Typography>
        </Toolbar>
      </AppBar>

      <div style={{ paddingTop: '64px', height: 'calc(100vh - 128px)' }}>
        <Grid container spacing={3}>
          {hasStatus && (
            <Grid item xs={1}>
              <Checkbox
                className={classes.checkbox}
                icon={<UncheckedIcon className={classes.sizeCheckboxIcon} />}
                checkedIcon={
                  <CheckedIcon className={classes.sizeCheckboxIcon} />
                }
                onChange={e => setChecked(e.target.checked)}
                checked={checked}
              />
            </Grid>
          )}
          <Grid item xs={hasStatus ? 11 : 12}>
            <TextField
              className={clsx(classes.margin, classes.fullWidth)}
              required
              placeholder='Summary'
              value={name}
              variant='outlined'
              onChange={handleChangeName}
              inputProps={{
                'aria-label': 'todo-summary',
                'data-lpignore': 'true',
                maxLength: MAX_LENGTH_TODO_SUMMARY
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              className={clsx(classes.margin, classes.fullWidth)}
              multiline
              rows={4}
              rowsMax={15}
              placeholder='Details...'
              value={description}
              variant='outlined'
              onChange={handleChangeDescription}
              inputProps={{
                'aria-label': 'todo-description',
                'data-lpignore': 'true',
                maxLength: MAX_LENGTH_TODO_DESCRIPTION
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TodoTagInput
              className={clsx(classes.margin, classes.fullWidth)}
              tags={tags}
              getAutocompleteTags={getAvailableTags}
              onSetTags={handleSetTags}
            />
          </Grid>
          <Grid item xs={12}>
            <AreaSelect
              decrypt={decrypt}
              getAreas={getAreas}
              classes={{
                areaSelect: clsx(classes.margin, classes.fullWidth)
              }}
              area={area}
              onSetArea={handleSetArea}
            />
          </Grid>
          {renderProjects()}
          {hasList && (
            <Grid item xs={12}>
              <FormControl className={clsx(classes.margin, classes.fullWidth)}>
                <InputLabel shrink>Focus</InputLabel>
                <Select
                  MenuProps={{
                    disablePortal: true
                  }}
                  value={list}
                  label='List'
                  onChange={handleSelectList}
                  onClick={e => {
                    if (e.target.value === 0) {
                      if (list === TICKLER_LIST) {
                        // Special case, we've already set a schedule,
                        // but are now trying to change it
                        setOpenStartPicker(true)
                      } else {
                        // Any other move should not have a start date
                        setStartDate(null)
                      }
                    }
                  }}
                  renderValue={value => {
                    if (
                      startDate &&
                      startDate.isValid() &&
                      startDate.isAfter(TODAY)
                    ) {
                      return startDate.format('LL')
                    } else {
                      return getFocusTitle(value)
                    }
                  }}
                >
                  {Object.entries(SELECTABLE_FOCUS).map(([id, item]) => (
                    <MenuItem key={id} value={item.id}>
                      {item.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <RepeatInput
              classes={{
                margin: classes.margin,
                fullWidth: classes.fullWidth
              }}
              onChangeRepeat={handleChangeRepeat}
              repeat={repeatPattern}
            />
          </Grid>
          <Grid item xs={12}>
            <TodoListItemAttachments
              classes={{
                margin: classes.margin,
                fullWidth: classes.fullWidth
              }}
              createAttachment={(file, progressCallback, completeCallback) =>
                createAttachment(todo, file, progressCallback, completeCallback)
              }
              deleteAttachment={attachment =>
                deleteAttachment(attachment, todo)
              }
              downloadAttachment={downloadAttachment}
              editAttachment={editAttachment}
              attachments={attachments}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant='contained'
              color='primary'
              label='Make Project'
              className={classes.margin}
              onClick={e => handleConvertToProject()}
            >
              Convert to Project
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant='contained'
              label='Delete'
              className={clsx(classes.margin, classes.delete)}
              color='secondary'
              startIcon={<DeleteIcon />}
              onClick={e => handleDelete()}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      </div>
      <DatePicker
        open={openStartPicker}
        portrait={useMediaQuery(theme.breakpoints.down('xs'), {
          noSsr: true
        })}
        onSave={date => {
          setStartDate(date)
          setOpenStartPicker(false)
        }}
        onClose={() => {
          setList(todo.list)
          setOpenStartPicker(false)
        }}
      />
      <ConfirmDelete
        trash={!DELETE_PERMANENTLY_FOCUS.includes(todo.list)}
        open={openConfirmDelete}
        deleteItem={decrypt(name)}
        onOk={handleDeleteOk}
        onCancel={useCallback(() => setOpenConfirmDelete(false), [])}
      />
      <Confirm
        title='Confirm Convert to Project'
        message={
          <Typography component='span'>
            Are you sure you want to convert{' '}
            <em>
              <b>{decrypt(name)}</b>
            </em>
            , this cannot be undone
          </Typography>
        }
        open={openConfirmConvertToProject}
        onOk={useCallback(async () => {
          await doConvertToProject()
          setOpenConfirmConvertToProject(false)
        }, [doConvertToProject])}
        onCancel={useCallback(() => setOpenConfirmConvertToProject(false), [])}
      />
    </Autosave>
  )
}

const TodoListItemEdit = ({
  classes: classProps,
  open,
  todo,
  onClose,
  hasStatus = true,
  hasList = true,
  decrypt,
  getRepeatPattern,
  getProjectsForAreas,
  clientId,
  createProject,
  deleteTodo,
  redirectToProject,
  getProjectById,
  editTodo,
  getAvailableTags,
  getAreas,
  getTagsFromTodo,
  createAttachment,
  deleteAttachment,
  editAttachment,
  downloadAttachment,
  getTodoAttachments
}) => {
  LOG.debug('Render: TodoListItemEdit')

  const theme = useTheme()
  const classes = useStyles(classProps)

  const [saveAllAndClose, setSaveAllAndClose] = useState(false)

  const handleCloseDrawer = useCallback(() => {
    setSaveAllAndClose(true)
  }, [])

  return (
    <Drawer
      style={{ zIndex: theme.zIndex.drawer + 1 }} // This is 1 behind the settings drawer, in case we need to show the subscription plans
      anchor='right'
      open={open}
      onClose={handleCloseDrawer}
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper
      }}
      SlideProps={{ mountOnEnter: true, unmountOnExit: true }}
    >
      <TodoListItemEditContent
        classes={classes}
        hasStatus={hasStatus}
        hasList={hasList}
        saveAllAndClose={saveAllAndClose}
        setSaveAllAndClose={setSaveAllAndClose}
        handleCloseDrawer={handleCloseDrawer}
        todo={todo}
        decrypt={decrypt}
        getRepeatPattern={getRepeatPattern}
        clientId={clientId}
        deleteTodo={deleteTodo}
        redirectToProject={redirectToProject}
        createProject={createProject}
        onClose={onClose}
        getProjectById={getProjectById}
        editTodo={editTodo}
        getProjectsForAreas={getProjectsForAreas}
        getAvailableTags={getAvailableTags}
        getAreas={getAreas}
        getTagsFromTodo={getTagsFromTodo}
        createAttachment={createAttachment}
        deleteAttachment={deleteAttachment}
        editAttachment={editAttachment}
        downloadAttachment={downloadAttachment}
        getTodoAttachments={getTodoAttachments}
      />
    </Drawer>
  )
}

export default memo(TodoListItemEdit)
